import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

function getAccessToken() {
  return localStorage.getItem('accessToken');
}

function getRefreshToken() {
  return localStorage.getItem('refreshToken');
}

function base64UrlDecodeToJson(base64Url) {
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

  // atob expects "binary string"
  const json = atob(padded);
  return JSON.parse(json);
}

function isJwtExpiredOrNearExpiry(token, skewSeconds = 30) {
  if (!token) return true;
  const parts = token.split('.');
  if (parts.length !== 3) return true;

  try {
    const payload = base64UrlDecodeToJson(parts[1]);
    const expSeconds = payload?.exp;
    if (!expSeconds) return true;

    const nowSeconds = Math.floor(Date.now() / 1000);
    return expSeconds <= nowSeconds + skewSeconds;
  } catch (e) {
    // If we can't decode, treat as "unknown" -> don't force refresh here.
    return false;
  }
}

function hasUsableAccessToken() {
  const token = getAccessToken();
  if (!token) return false;

  const parts = token.split('.');
  if (parts.length !== 3) return false;

  try {
    const payload = base64UrlDecodeToJson(parts[1]);
    const expSeconds = payload?.exp;
    if (!expSeconds) return false;

    const nowSeconds = Math.floor(Date.now() / 1000);
    return expSeconds > nowSeconds + 30;
  } catch {
    return false;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);

  const ensureFreshAccessToken = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    const accessToken = getAccessToken();
    const needsRefresh = !accessToken || isJwtExpiredOrNearExpiry(accessToken, 30);
    if (!needsRefresh) return true;

    try {
      await authService.refresh(refreshToken);
      return true;
    } catch (e) {
      // Refresh failed -> clear tokens + user state
      authService.logout();
      setUser(null);
      return false;
    }
  }, []);

  const loadMe = useCallback(async () => {
    const hasAnyToken = !!getAccessToken() || !!getRefreshToken();
    if (!hasAnyToken) {
      setUser(null);
      return null;
    }

    // Proactively refresh if needed before /me
    await ensureFreshAccessToken();

    try {
      const me = await authService.me();
      setUser(me);
      return me;
    } catch (e) {
      // If /me fails (401 etc.), last attempt: refresh + retry once
      const refreshed = await ensureFreshAccessToken();
      if (refreshed) {
        try {
          const me = await authService.me();
          setUser(me);
          return me;
        } catch (e2) {
          // fall through
        }
      }

      setUser(null);
      return null;
    }
  }, [ensureFreshAccessToken]);

  useEffect(() => {
    const onLogout = () => {
      // tokens already cleared by api.js; keep state consistent
      setUser(null);
    };

    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadMe();
      } finally {
        setInitializing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      user,
      initializing,
      isAuthenticated: !!user || hasUsableAccessToken(),

      refreshMe: loadMe,

      login: async (email, password, otpCode) => {
        await authService.login(email, password, otpCode);
        return loadMe();
      },

      logout: () => {
        authService.logout();
        setUser(null);
      },
    }),
    [user, initializing, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
